import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const ACCORDION_ANIMATION_DURATION = 600;
const ACCORDION_ANIMATION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get #transitionPanel() {
    const panel = this.querySelector(':scope > details > [data-accordion-transition-panel]');

    return panel instanceof HTMLElement ? panel : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #disableAnimationOnDesktop() {
    return this.dataset.disableAnimationOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #isStaticRow() {
    const accordion = this.parentElement;

    return accordion instanceof HTMLElement && accordion.classList.contains('accordion--single-row');
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  #isClosing = false;

  /** @type {(() => void) | null} */
  #panelTransitionCleanup = null;

  connectedCallback() {
    if (!(this.querySelector(':scope > details') instanceof HTMLDetailsElement)) return;

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.classList.add('accordion-custom--js-motion');
    this.classList.toggle('accordion-custom--static', this.#isStaticRow);

    if (this.#isStaticRow) {
      this.summary.setAttribute('aria-disabled', 'true');
      this.summary.tabIndex = -1;
    } else {
      this.summary.removeAttribute('aria-disabled');
      this.summary.removeAttribute('tabindex');
    }

    this.#setDefaultOpenState();
    this.classList.toggle('accordion-custom--expanded', this.details.open);

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#cancelPanelTransition();
    this.#stopAnimation();
    this.#resetAnimationStyles();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (this.#isStaticRow || (isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.setOpen(!(this.details.open && !this.#isClosing));
  };

  /**
   * Opens or closes the accordion with a measured-height animation. Keeping
   * the details element open until a close animation finishes avoids the
   * native instant content removal that causes the surrounding layout to jump.
   *
   * @param {boolean} shouldOpen - The target state.
   * @param {{ animate?: boolean }} [options] - Animation options.
   */
  setOpen(shouldOpen, { animate = true } = {}) {
    if (this.#isStaticRow && !shouldOpen) return;

    const isOpen = this.details.open && !this.#isClosing;

    if (shouldOpen === isOpen && !this.#animation && !this.#panelTransitionCleanup) return;

    if (shouldOpen) this.#closeSiblingAccordions();

    if (this.#transitionPanel) {
      this.#setTransitionPanelOpen(shouldOpen, { animate });
      return;
    }

    if (!animate || this.#shouldSkipAnimation()) {
      this.#stopAnimation();
      this.#isClosing = false;
      this.details.open = shouldOpen;
      this.classList.toggle('accordion-custom--expanded', shouldOpen);
      this.#resetAnimationStyles();
      return;
    }

    if (shouldOpen) {
      this.#expand();
    } else {
      this.#collapse();
    }
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.setOpen(false);
  };

  #handleToggle = () => {
    if (this.#animation || this.#isClosing) return;

    this.classList.toggle('accordion-custom--expanded', this.details.open);

    if (this.details.open) this.#closeSiblingAccordions();
  };

  /**
   * Uses an inner grid panel for product information rows. Unlike animating
   * the outer details height, this keeps the summary, focus outline, and
   * divider geometry stable while the content changes the page height.
   *
   * @param {boolean} shouldOpen - The target state.
   * @param {{ animate?: boolean }} [options] - Animation options.
   */
  #setTransitionPanelOpen(shouldOpen, { animate = true } = {}) {
    const panel = this.#transitionPanel;

    if (!panel) return;

    this.#cancelPanelTransition();
    this.#stopAnimation();
    this.#resetAnimationStyles();

    if (!animate || this.#shouldSkipPanelAnimation()) {
      this.#isClosing = false;
      this.classList.toggle('accordion-custom--expanded', shouldOpen);
      this.details.open = shouldOpen;
      return;
    }

    if (shouldOpen) {
      if (!this.details.open) {
        this.details.open = true;
        panel.getBoundingClientRect();
      }

      this.#isClosing = false;
      this.classList.add('accordion-custom--expanded');
      return;
    }

    if (!this.details.open) {
      this.#isClosing = false;
      this.classList.remove('accordion-custom--expanded');
      return;
    }

    this.#isClosing = true;
    this.classList.remove('accordion-custom--expanded');

    let fallbackTimer;

    const cleanup = () => {
      panel.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(fallbackTimer);

      if (this.#panelTransitionCleanup === cleanup) this.#panelTransitionCleanup = null;
    };

    const finishTransition = () => {
      cleanup();

      if (!this.classList.contains('accordion-custom--expanded')) this.details.open = false;

      this.#isClosing = false;
    };

    const handleTransitionEnd = (event) => {
      if (event.target === panel && event.propertyName === 'grid-template-rows') finishTransition();
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
    fallbackTimer = window.setTimeout(finishTransition, 750);
    this.#panelTransitionCleanup = cleanup;
  }

  #cancelPanelTransition() {
    this.#panelTransitionCleanup?.();
    this.#panelTransitionCleanup = null;
  }

  #expand() {
    const { details } = this;
    const startHeight = details.getBoundingClientRect().height;

    this.#stopAnimation();
    details.style.height = startHeight + 'px';
    details.style.overflow = 'hidden';

    if (!details.open) details.open = true;

    this.#isClosing = false;
    this.classList.add('accordion-custom--expanded');
    this.#animateHeight(startHeight, details.scrollHeight, true);
  }

  #collapse() {
    const { details } = this;

    if (!details.open) {
      this.#finishAnimation(false);
      return;
    }

    const startHeight = details.getBoundingClientRect().height;

    this.#stopAnimation();
    details.style.height = startHeight + 'px';
    details.style.overflow = 'hidden';
    this.#isClosing = true;
    this.classList.remove('accordion-custom--expanded');
    this.#animateHeight(startHeight, this.summary.getBoundingClientRect().height, false);
  }

  /**
   * @param {number} startHeight - Current rendered height.
   * @param {number} endHeight - Target rendered height.
   * @param {boolean} shouldOpen - Target state.
   */
  #animateHeight(startHeight, endHeight, shouldOpen) {
    if (Math.abs(endHeight - startHeight) < 1) {
      this.#finishAnimation(shouldOpen);
      return;
    }

    this.#animation = this.details.animate(
      [{ height: startHeight + 'px' }, { height: endHeight + 'px' }],
      {
        duration: ACCORDION_ANIMATION_DURATION,
        easing: ACCORDION_ANIMATION_EASING,
      }
    );

    this.#animation.onfinish = () => this.#finishAnimation(shouldOpen);
  }

  /**
   * @param {boolean} shouldOpen - Final state after the animation.
   */
  #finishAnimation(shouldOpen) {
    this.#animation = null;
    this.#isClosing = false;
    this.classList.toggle('accordion-custom--expanded', shouldOpen);

    if (!shouldOpen) this.details.open = false;

    this.#resetAnimationStyles();
  }

  #stopAnimation() {
    if (!this.#animation) return;

    this.#animation.onfinish = null;
    this.#animation.cancel();
    this.#animation = null;
  }

  #resetAnimationStyles() {
    const details = this.querySelector(':scope > details');

    if (!(details instanceof HTMLDetailsElement)) return;

    details.style.removeProperty('height');
    details.style.removeProperty('overflow');
  }

  #shouldSkipAnimation() {
    const animationUnsupported = typeof this.details.animate !== 'function';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationDisabledForViewport = !isMobileBreakpoint() && this.#disableAnimationOnDesktop;

    return animationUnsupported || reducedMotion || animationDisabledForViewport;
  }

  #shouldSkipPanelAnimation() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationDisabledForViewport = !isMobileBreakpoint() && this.#disableAnimationOnDesktop;

    return reducedMotion || animationDisabledForViewport;
  }

  #closeSiblingAccordions() {
    const accordion = this.closest('.accordion');

    if (!(accordion instanceof HTMLElement)) return;

    for (const accordionItem of accordion.querySelectorAll(':scope > accordion-custom')) {
      if (!(accordionItem instanceof AccordionCustom) || accordionItem === this) continue;
      if (!(accordionItem.querySelector(':scope > details') instanceof HTMLDetailsElement)) continue;

      accordionItem.setOpen(false);
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    if (this.#isStaticRow) {
      this.setOpen(true, { animate: false });
      return;
    }

    const isMobile = isMobileBreakpoint();

    this.setOpen(
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
        (!isMobile && this.hasAttribute('open-by-default-on-desktop')),
      { animate: false }
    );
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.setOpen(false);
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
